import React, { useRef, useState } from 'react';
import { NewModal } from 'lib/components';
import { getCroppedImageFile } from 'lib/components/image-crop-editor/getCroppedImageFile';
import { ImageCropEditor } from 'lib/components/image-crop-editor/ImageCropEditor';
import { Flex } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { Crop, PixelCrop } from 'react-image-crop';
import { FileDropZone } from 'lib/components/inputs/FileDropZone/FileDropZone';
import {
  FileInfo,
  FileLoadedData,
} from 'lib/components/inputs/FileDropZone/constants';
import styled from 'styled-components/macro';

interface ImageUploadAndCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (imageFile: File) => Promise<void> | void;
  isSaving: boolean;
  headerText: string;
  saveButtonText: string;
  aspectRatio?: number;
  targetWidth?: number;
  useCropper?: boolean;
}

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
`;

export const ImageUploadAndCropModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  headerText,
  saveButtonText,
  aspectRatio,
  targetWidth,
  useCropper = true,
}: ImageUploadAndCropModalProps) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [previewCanvas, setPreviewCanvas] = useState<HTMLCanvasElement | null>(
    null
  );
  const [isCropping, setIsCropping] = useState(false);
  const [scale, setScale] = useState(1);
  const [imgSrc, setImgSrc] = useState('');
  const [fileName, setFileName] = useState('');
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  const resetImageSelection = () => {
    setImgSrc('');
    setFileName('');
    setOriginalFile(null);
    // Reset cropper state
    setCrop(undefined);
    setCompletedCrop(undefined);
    setScale(1);
    setIsCropping(false);
  };

  const handleClose = () => {
    resetImageSelection();
    onClose();
  };

  const handleSave = async () => {
    if (useCropper) {
      if (!previewCanvas || !completedCrop) return;
      setIsCropping(true);
      const croppedImageFile = await getCroppedImageFile({
        previewCanvas,
        fileName,
        targetWidth,
        quality: 1,
      });
      if (croppedImageFile) {
        await onSave(croppedImageFile);
      }
    } else {
      if (!originalFile) return;
      await onSave(originalFile);
    }
    resetImageSelection();
  };

  const handleFileLoaded = (
    data: FileLoadedData,
    fileInfo: FileInfo,
    file: File
  ) => {
    if (file.type.startsWith('image/')) {
      setImgSrc(data as string);
      setFileName(fileInfo.name);
      if (!useCropper) {
        setOriginalFile(file);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const isSaveDisabled =
    !imgSrc ||
    isSaving ||
    isCropping ||
    (useCropper && !completedCrop) ||
    (!useCropper && !originalFile);

  console.log(useCropper);

  return (
    <NewModal
      headerText={headerText}
      closeModal={isSaving ? () => {} : handleClose}
      style={{
        content: { width: '528px', maxWidth: '528px', padding: '32px' },
      }}
    >
      <Flex flexDirection='column' gap='32px'>
        {!!imgSrc ? (
          <>
            {useCropper ? (
              <ImageCropEditor
                src={imgSrc}
                imageRef={imageRef}
                crop={crop}
                setCrop={setCrop}
                onCropComplete={setCompletedCrop}
                completedCrop={completedCrop}
                scale={scale}
                setScale={setScale}
                disabled={isSaving}
                onPreviewChange={setPreviewCanvas}
                aspect={aspectRatio}
              />
            ) : (
              <Flex justifyContent='center' alignItems='center'>
                <ImagePreview src={imgSrc} alt='Image preview' />
              </Flex>
            )}
          </>
        ) : (
          <FileDropZone
            accept={['image/*']}
            onFileLoaded={handleFileLoaded}
            extendStyles={{ container: { height: '360px' } }}
          />
        )}
        <Flex
          flexDirection='row'
          justifyContent='flex-end'
          alignItems='center'
          gap='8px'
        >
          {imgSrc && (
            <Button
              text='Replace image'
              variant='secondary'
              onClick={resetImageSelection}
              disabled={isSaving || isCropping}
            />
          )}
          <Button
            text={saveButtonText}
            onClick={handleSave}
            loading={isSaving || isCropping}
            disabled={isSaveDisabled}
          />
        </Flex>
      </Flex>
    </NewModal>
  );
};
